import { Fragment, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as companyApi from '../api/Company';
import { ICompany, ICompanyAndBilling, ICompanyBilling, IUserProfile } from '../util/DataModelInterface'
import { Spinner } from '../components/Spinner';
import { ToastWrap } from '../components/ToastWrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SearchModal } from '../components/SearchModal';
import { getAllUsers } from '../api/User';
import { ListGroup } from 'react-bootstrap';
import { ConfirmModal } from '../components/ConfirmModal';
import { getCodeList } from 'country-list';
import useToastHandler from '../hooks/useToastHandler';
import DatePicker from 'react-datepicker';
import { CalendarInput } from '../components/CalendarInput';
import { format } from 'date-fns';

interface ICompanyValidation {
    name: boolean;
    business_id: boolean;
    address: boolean;
    postal_code: boolean;
    city: boolean;
    country: boolean;
    phone: boolean;
    www: boolean;
    member: boolean;
    member_since: boolean;
    reporting_started: boolean;
    reporting_ended: boolean;
    reporting_interval: boolean;
    certificate: boolean;
    ledger_query: boolean;
}

interface ICompanyBillingValidation {
    name: boolean;
    name2: boolean;
    name3: boolean;
    address: boolean;
    postal_code: boolean;
    city: boolean;
    country: boolean;
    reference:  boolean;
    vat_rate: boolean;
    payment_term: boolean;
    type: boolean;
    email: boolean;
    einvoice_operator: boolean;
    einvoice_id: boolean;
    period: boolean;
    lang: boolean;
}

export const Company = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { addToast } = useToastHandler();
    const lang = intl.locale;

    const [company, setCompany] = useState(state.company as ICompanyAndBilling);
    const [users, setUsers] = useState([] as IUserProfile[]);
    const [activeUsers, setActiveUsers] = useState([] as IUserProfile[]);
    const [countries, setCountries] = useState({} as {[name: string]: string});
    const [selectedUsers, setSelectedUsers] = useState([] as IUserProfile[]);
    const [saving, setSaving] = useState(false);
    const [expandDetails, setExpandDetails] = useState(false);
    const [expandInvoicing, setExpandInvoicing] = useState(false);
    const [expandMembership, setExpandMembership] = useState(false);
    const [expandUsers, setExpandUsers] = useState(false);
    const [changedDetails, setChangedDetails] = useState({
      details: false,
      invoicing: false,
      membership: false,
      users: false
    });
    const [changed, setChanged] = useState(false);
    // const [isValidForm, setIsValidForm] = useState(true);
    // const [userExistsError, setUserExistsError] = useState(false);
    const [toastActive, setToastActive] = useState(false);
    const [toastHeader, setToastHeader] = useState("");
    const [toastText, setToastText] = useState("");
    const [toastType, setToastType] = useState("");
    const [modalActive, setModalActive] = useState(false);
    const [confirmModalActive, setConfirmModalActive] = useState(false);
    const [removedItem, setRemovedItem] = useState({} as IUserProfile);
    const [isArchive, setIsArchive] = useState(false);
    const [confirmArchModalActive, setConfirmArchModalActive] = useState(false);
    const [confirmModalTitle, setConfirmModalTitle] = useState("");
    const [confirmModalContent, setConfirmModalContent] = useState("");
    const [companyState, setCompanyState] = useState({
        id: company?.id,
        name: company?.name ? company.name : "",
        business_id: company?.business_id ? company.business_id : "",
        address: company?.address ? company.address : "",
        postal_code: company?.postal_code ? company.postal_code : "",
        city: company?.city ? company.city : "",
        country: company?.country ? company.country : "FI",
        phone: company?.phone ? company.phone : "",
        www: company?.www ? company.www : "",
        member: company?.member ? company.member : "",
        member_since: company?.member_since ? company.member_since : "",
        reporting_started: company?.reporting_started ? company.reporting_started : "",
        reporting_ended: company?.reporting_ended ? company.reporting_ended : "",
        reporting_interval: company?.reporting_interval ? company.reporting_interval : "1",
        certificate: company?.certificate ? company.certificate : "",
        archived: company?.archived ? company.archived : false,
        archive_period: company?.archive_period ? company.archive_period : "",
    } as ICompany);
    const [companyBillingState, setCompanyBillingState] = useState({} as ICompanyBilling);

    const [companyStateClass, setCompanyStateClass] = useState({
        name: "",
        business_id: "",
        address: "",
        postal_code: "",
        city: "",
        country: "",
        phone: "",
        www: "",
        member: "",
        member_since: "",
        reporting_started: "",
        reporting_ended: "",
        reporting_interval: "",
        certificate: "",
        archived: "",
        archive_period: ""
    });

    const [companyBillingStateClass, setCompanyBillingStateClass] = useState({
        name: "",
        name2: "",
        name3: "",
        address: "",
        postal_code: "",
        city: "",
        country: "",
        reference:  "",
        vat_rate: "",
        payment_term: "",
        type: "",
        email: "",
        einvoice_operator: "",
        einvoice_id: "",
        ledger_query: "",
        period: "",
        lang: "",
    });

    useEffect(() => {
      let countryNames = getCodeList();
      // console.log("countryNames", countryNames);
      setCountries(countryNames);
    }, []);

    useEffect(() => {
      if (company?.id) {
        companyApi.getCompanyBilling(parseInt(company.id)).then((data: any) => {
            if (data?.error) {
              addToast(data.error, 'companies', 'fetch');
            } else if (data) {
              setCompanyBillingState(data);
            }
        }).finally(() => {
          update();
        });
      } else {
        setCompanyBillingState({
          // company_id: undefined,
          name: "",
          name2: "",
          name3: "",
          address: "",
          postal_code: "",
          city: "",
          country: "FI",
          reference:  "",
          vat_rate: "1",
          payment_term: "15",
          type: "1",
          email: "",
          einvoice_operator: "",
          einvoice_id: "",
          ledger_query: "",
          period: "1",
          lang: "fi"
        });
        update();
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [company?.id]);

    const update = async (force: boolean = false) => {
      await getAllUsers(force).then((data: any) => {
          if (data?.error) {
            addToast(data.error, 'users', 'fetch');
          } else if (data) {
            setUsers(data);
            setActiveUsers(data.filter((user: IUserProfile) => user.archived === false));
          }
      });
    }

    const comparer = (a: any, b: any) => {
      return a.name.localeCompare(b.name);
    }

    useEffect(() => {
      let compUsers = [] as IUserProfile[];
      if (users.length > 0 && selectedUsers.length === 0 && company?.id) {
        companyApi.getCompany(company.id).then((data: any) => {
          if (data?.error) {
            addToast(data.error, 'companies', 'fetch');
          } else if (data) {
            let comp = data as ICompany;
            comp.users?.forEach((u_id) => {
              let user = users.find((u: any) => u["user_id"] === u_id);
              if (user?.user_id) compUsers.push(user);
            })
            compUsers.sort(comparer);
            setSelectedUsers(compUsers);
          }
      });

      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users]);

    const isValidEmail = (email: string) => {
      // const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      // eslint-disable-next-line no-useless-escape
      const regex = new RegExp('^[^@<> ]+@[^@<> ]+\.[^@<> ]+');
      return regex.test(String(email).toLowerCase())
    }

    const validationRules: ICompanyValidation = {
        name: !!companyState.name && companyState.name.match(/^ *$/) === null,
        business_id: !!companyState.business_id && companyState.business_id.match(/^ *$/) === null,
        address: true,
        postal_code: true,
        city: true,
        country: true,
        phone: true,
        www: true,
        member: !!companyState.member && companyState.member.match(/^ *$/) === null,
        member_since: !!companyState.member_since && companyState.member_since.match(/^ *$/) === null,
        reporting_started: !!companyState.reporting_started && companyState.reporting_started.match(/^ *$/) === null,
        reporting_ended: true,
        reporting_interval: true,
        certificate: !!companyState.certificate && companyState.certificate.match(/^ *$/) === null,
        ledger_query: true
    }

    const validationBillingRules: ICompanyBillingValidation = {
        name: true,
        name2: true,
        name3: true,
        address: true,
        postal_code: true,
        city: true,
        country: true,
        reference:  true,
        vat_rate: true,
        payment_term: true,
        type: true,
        email: (!companyBillingState.email) || (!!companyBillingState.email && isValidEmail(companyBillingState.email)),
        einvoice_operator: true,
        einvoice_id: true,
        period: true,
        lang: true,
      }

    const updateCompany = (event: { currentTarget: any; }) => {
      const target = event.currentTarget;

      setCompanyState({
          ...companyState,
          [target.name]: target.type === 'checkbox'
              ? target.checked
              : target.value
      })
      setCompanyStateClass({...companyStateClass, [target.name]: 'fieldchanged'});
      setChanged(true);
    }

    const updateBilling = (event: { currentTarget: any; }) => {
        const target = event.currentTarget;
  
        setCompanyBillingState({
            ...companyBillingState,
            [target.name]: target.type === 'checkbox'
                ? target.checked
                : target.value
        })
        setCompanyBillingStateClass({...companyBillingStateClass, [target.name]: 'fieldchanged'});
        setChanged(true);
    }

    const updateClasses = (classState: { [x: string]: string; }) => {
      Object.keys(classState).forEach(k => {
        if (classState[k] === 'fieldchanged') {
          classState[k] = 'fieldsaved';
        }
      });
    }

    // Set states to saved
    const setSavedStates = () => {

        let newCompanyStateClass = companyStateClass as any;
        updateClasses(newCompanyStateClass);
        setCompanyStateClass(newCompanyStateClass);

        let newCompanyBillingStateClass = companyBillingStateClass as any;
        updateClasses(newCompanyBillingStateClass);
        setCompanyBillingStateClass(newCompanyBillingStateClass);

        setCompany({...company, name: companyState.name});
        setToastHeader(intl.formatMessage({id: 'companies.saved'}));
        setToastText("");
        setToastType('success');
        setToastActive(true);

        setChangedDetails({
          details: false,
          invoicing: false,
          membership: false,
          users:false
        });
    }

    const saveCompany = async (e: { preventDefault: () => void; } | undefined) => {

      e?.preventDefault(); // needed?

      const isValidForm = Object
        .values(validationRules)
        .every(key => key) &&
        Object
        .values(validationBillingRules)
        .every(key => key)

      // setIsValidForm(isValidForm);

      if (!isValidForm) {
          // console.log('Not valid form');
          if(!companyState.id) {
            setToastHeader(intl.formatMessage({id: 'error.companies.add'}));
          } else {
            setToastHeader(intl.formatMessage({id: 'error.companies.modify'}));
          }
          setToastText(intl.formatMessage({id: 'companies.fail.info'}));
          setToastType('warning');
          setToastActive(true);
          return;
      }

      let companyBilling = {
        // company_id: companyBillingState.company_id,
        name: companyBillingState.name ? companyBillingState.name : "",
        name2: companyBillingState.name2 ? companyBillingState.name2 : "",
        name3: companyBillingState.name3 ? companyBillingState.name3 : "",
        address: companyBillingState.address ? companyBillingState.address : "",
        postal_code: companyBillingState.postal_code ? companyBillingState.postal_code : "",
        city: companyBillingState.city ? companyBillingState.city : "",
        country: companyBillingState.country ? companyBillingState.country : "FI",
        reference: companyBillingState.reference ? companyBillingState.reference : "",
        vat_rate: companyBillingState.vat_rate ? companyBillingState.vat_rate : "0",
        payment_term: companyBillingState.payment_term ? companyBillingState.payment_term : "15",
        type: companyBillingState.type ? companyBillingState.type : "1",
        email: companyBillingState.email ? companyBillingState.email : "",
        einvoice_operator: companyBillingState.einvoice_operator ? companyBillingState.einvoice_operator : "",
        einvoice_id: companyBillingState.einvoice_id ? companyBillingState.einvoice_id : "",
        ledger_query: companyBillingState.ledger_query ? companyBillingState.ledger_query : "",
        period: companyBillingState.period ? companyBillingState.period : "",
        lang: companyBillingState.lang ? companyBillingState.lang : "fi"
      } as ICompanyBilling;

      let company = {
        name: companyState.name, // mandatory
        business_id: companyState.business_id, // mandatory
        address: companyState.address ? companyState.address : "",
        postal_code: companyState.postal_code ? companyState.postal_code : "",
        city: companyState.city ? companyState.city : "",
        country: companyState.country ? companyState.country : "FI",
        phone: companyState.phone ? companyState.phone : "",
        www: companyState.www ? companyState.www : "",
        archived: companyState.archived ? companyState.archived : "false",
        archive_period: companyState.archive_period ? companyState.archive_period : "",
        id: companyState.id, // mandatory
        member: companyState.member, // mandatory
        member_since: companyState.member_since ? companyState.member_since : "",
        reporting_started: companyState.reporting_started ? companyState.reporting_started : "",
        reporting_ended: companyState.reporting_ended ? companyState.reporting_ended : "",
        reporting_interval: companyState.reporting_interval ? companyState.reporting_interval : "1",
        certificate: companyState.certificate, // mandatory
        users: [],
        billing: companyBilling
      } as ICompanyAndBilling;

      setSaving(true);

      if (!companyState.id) { // Company does not exist yet
        let resp = await companyApi.addCompany(company);
        if (resp?.error) {
          addToast(resp.error, 'companies', 'add');
        } else {
          let id = resp.id;
          // console.log("id", id);
          setCompanyState({...companyState, id: id});
          setSavedStates();
          setChanged(false);
        }
      } else { // Modify company
        // add user ids
        let userIds = [] as number[];
        selectedUsers.forEach(((s) => {
          let user = users.find((u: any) => u["user_id"] === s.user_id);
          if (user?.user_id) userIds.push(parseInt(user.user_id.toString())); // To get integers right
        }));
        company.users = userIds;
        let resp = await companyApi.addCompany(company);
        if (resp?.error) {
          addToast(resp.error, 'companies', 'modify');
        } else {
          await update(true); // Update users to get correct company list per user
          setSavedStates();
          setChanged(false);
        }
      }
      setSaving(false);
    }

    const validateCompany = (field: keyof ICompanyValidation) => {
      return (!validationRules[field] ? 'has-error' : '')
    }

    const validateBilling = (field: keyof ICompanyBillingValidation) => {
        return (!validationBillingRules[field] ? 'has-error' : '')
      }

    const handleSelectedItems = (sel: IUserProfile[]) => {
      let newSelectedUsers = selectedUsers;
      sel.forEach((s, i) => {
        if (!newSelectedUsers.find(n => n.user_id === s.user_id)) newSelectedUsers.push(s);
      });
      setSelectedUsers(newSelectedUsers.sort(comparer));
      setChanged(true);
      setChangedDetails({...changedDetails, users: true});
    }

    const removeItem = (item: IUserProfile) => {
      let newSelectedUsers = selectedUsers.filter((u) => u.user_id !== item.user_id);
      setSelectedUsers(newSelectedUsers);
      setChanged(true);
      setChangedDetails({...changedDetails, users: true});
    }

    const confirmRemoveItem = (item: IUserProfile) => {
      setConfirmModalActive(true);
      setRemovedItem(item);
    }

    const getCountrySelectList = () => {
      const options: JSX.Element[] = [];
      Object.keys(countries).forEach((code) =>
        options.push(
          <option key={code} value={code.toUpperCase()}>{countries[code]}</option>
      ));
      return(options);
    }

    const getUserItem = (item: IUserProfile, ind: number) => {
      let user = users.find((u) => u.user_id === item.user_id);
      return (
        <Fragment key={ind}>
        { user ?
          <ListGroup.Item className="d-flex flex-row align-items-center">
            <div className="company">
              <span className="title">
                <Link to='/usermanagement/user' state={{ user }} replace={false}>
                  { user.name }
                </Link>
                <span className="ms-2">
                  { user.user_name }
                </span>
              </span>
            </div>
            <button className="btn btn-link p-0 my-0 ms-auto" onClick={() => confirmRemoveItem(item)}>
              <i className="me-2 fa fa-trash"></i>
              <FormattedMessage id="button.remove"/>
            </button>
          </ListGroup.Item>
          : null }
        </Fragment>
      );
    }

    const archiveComp = async (archive: boolean) => {

      let comp =  {
        name: companyState.name,
        id: companyState.id,
        reporting_ended: companyState.reporting_ended,
        archived: archive
      } as ICompany;

      setSaving(true);
      let resp = await companyApi.archiveCompany(comp);
      if (resp?.error) {
        addToast(resp.error, 'companies', 'archive');
      } else {
        setCompanyState({...companyState, archived: archive});
      }
      setSaving(false);
    }

    const confirmArchiveCompany = () => {
      let archived = !companyState.archived;
      setIsArchive(archived);

      let title = archived ? intl.formatMessage({id: 'confirm.archive'}) : intl.formatMessage({id: 'confirm.activate'});
      let textContent = archived ? intl.formatMessage({id: 'confirm.archive.content'}) : intl.formatMessage({id: 'confirm.activate.content'});

      setConfirmModalTitle(title);
      setConfirmModalContent(textContent);
      setConfirmArchModalActive(true);
    }

    const confirmContent = () => <div>{intl.formatMessage({id: 'confirm.remove.content'})}</div>;
    const confirmArchiveContent = () => <div>{confirmModalContent}</div>;

    const popperModifiers = [{name: "offset", options: {offset: [0, -20],},}];

    return (
      <>
        <SearchModal
          active={modalActive}
          // searchData={users.map( (c) => c.name )}
          searchData={activeUsers}
          modalId='addUserModal'
          onClose={() => setModalActive(false)}
          onSelect={(userList: IUserProfile[]) => handleSelectedItems(userList)}
          title={intl.formatMessage({id: 'companies.adduser'})}
          findText={intl.formatMessage({id: 'companies.finduser'})}
          placeHolder={intl.formatMessage({id: 'companies.pickuser'})}
          foundItemsTitle={intl.formatMessage({id: 'companies.userstoadd'})}
          filterByFields={['name']}
          />
        <ConfirmModal
          active={confirmModalActive}
          modalId='deleteItemModal'
          onClose={() => setConfirmModalActive(false)}
          title={intl.formatMessage({id: 'confirm.remove'})}
          content={confirmContent()}
          confirmbutton={
            {
              name: intl.formatMessage({id: 'confirm.remove'}),
              class: 'btn btn-primary',
              onClick: () => { removeItem(removedItem); setConfirmModalActive(false) }
            }
          }
          />
        <ConfirmModal
          active={confirmArchModalActive}
          modalId='arhciveCompanyModal'
          onClose={() => setConfirmArchModalActive(false)}
          title={confirmModalTitle}
          content={confirmArchiveContent()}
          confirmbutton={
            {
              name: confirmModalTitle,
              class: 'btn btn-primary',
              onClick: () => { archiveComp(isArchive); setConfirmArchModalActive(false) }
            }
          }
          />
        <Spinner active={saving} />
        <ToastWrap active={toastActive} heading={toastHeader} text={toastText} type={toastType} setActive={(active: boolean) => setToastActive(active)} />
        <div className="action-buttons py-4 d-flex flex-row align-items-start">
            <div className="page-header me-4">
            {(company?.name?.length > 0) ?
            <h1>{company.name}</h1> :
            <h1><FormattedMessage id="companies.add"/></h1>
            }
            </div>

            <div className="ms-auto d-flex flex-row align-items-center">
              { companyState.archived &&
                <p className="state me-4 m-0 d-flex flex-row align-items-center ">
                  <i className="fa fa-archive me-2"></i>
                  <FormattedMessage id="companies.archived"/>
                </p>
              }
              <button type="button" className="btn btn-secondary" onClick={() => { navigate(-1); }}>
                <i className="fa fa-close me-2"></i>
                <FormattedMessage id="button.close"/>
              </button>
              { !companyState.archived &&
                <button type="button" className="btn btn-secondary btn-archive"
                  onClick={() => confirmArchiveCompany()}>
                  <i className="fa fa-archive me-2"></i>
                  <FormattedMessage id="button.archive"/>
                </button>
              }
              { companyState.archived &&
                <button type="button" className="btn btn-secondary btn-activate"
                  onClick={(e) => confirmArchiveCompany()}>
                  <i className="fa fa-check me-2"></i>
                  <FormattedMessage id="button.activate"/>
                </button>
              }
              <button type="button" className="btn btn-primary btn-save" disabled={!changed}
                onClick={(e) => saveCompany(e)}>
                <i className="fa fa-save me-2"></i>
                <FormattedMessage id="button.save"/>
              </button>

            </div>

          </div>

          <div className="controls d-flex justify-content-end my-3">
            <button className="btn btn-link p-0"
              style={ !(expandDetails && expandInvoicing && expandMembership && expandUsers) ? {} : {display: 'none'}}
              onClick={ () => { setExpandUsers(true); setExpandMembership(true); setExpandInvoicing(true); setExpandDetails(true); }}>
              <FormattedMessage id="button.expandall" />
              <i aria-hidden="true" className="fa fa-chevron-down ms-2"></i>
            </button>
            <button className="btn btn-link p-0"
              style={ expandDetails && expandInvoicing && expandMembership && expandUsers ? {} : {display: 'none'}}
              onClick={ () => { setExpandUsers(false); setExpandMembership(false); setExpandInvoicing(false); setExpandDetails(false); }}>
              <FormattedMessage id="button.collapseall" />
              <i aria-hidden="true" className="fa fa-chevron-up ms-2"></i></button>
          </div>

          <details id="details" open={expandDetails}>
            <summary onClick={ (e) => { setExpandDetails(expandDetails => !expandDetails); e.preventDefault(); }}
              className={ changedDetails.details ? "changed" : "" }>
                <h2>
                  <FormattedMessage id="companies.details"/>
                </h2>
            </summary>
            <div>

              <div className={"form-group " + validateCompany('name')}>
                <label htmlFor="name" className="form-label"><FormattedMessage id="companies.name"/></label>
                <input id="name" name="name" className={"form-control " + companyStateClass.name}
                  type="text" value={companyState.name} autoComplete="off"
                  onChange={(e) => {
                    updateCompany(e);
                    setChangedDetails({...changedDetails, details: true});
                  }}/>
                <div aria-atomic="true" aria-live="polite">
                  <div className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="companies.obligatory.info"/>
                  </div>
                </div>
              </div>

              <div className={"form-group " + validateCompany('business_id')}>
                <label htmlFor="business_id" className="form-label"><FormattedMessage id="companies.bid"/></label>
                <input id="business_id" name="business_id" className={"form-control " + companyStateClass.business_id}
                  type="text" value={companyState.business_id} autoComplete="off"
                  onChange={(e) => {
                    updateCompany(e);
                    setChangedDetails({...changedDetails, details: true});
                  }}/>
                <div aria-atomic="true" aria-live="polite">
                  <div className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="companies.obligatory.info"/>
                  </div>
                </div>
              </div>

              <h2><FormattedMessage id="companies.contactinfo"/></h2>

              <div className={"form-group " + validateCompany('address')}>
                <label htmlFor="address" className="form-label"><FormattedMessage id="companies.address"/></label>
                <input id="address" name="address" className={"form-control " + companyStateClass.address}
                  type="text" value={companyState.address} autoComplete="off"
                  onChange={(e) => {
                    updateCompany(e);
                    setChangedDetails({...changedDetails, details: true});
                  }}/>
                <div aria-atomic="true" aria-live="polite">
                  <div className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="companies.obligatory.info"/>
                  </div>
                </div>
              </div>

              <div className={"form-group " + validateCompany('postal_code')}>
                <label htmlFor="postal_code" className="form-label"><FormattedMessage id="companies.postal_code"/></label>
                <input id="postal_code" name="postal_code" className={"form-control " + companyStateClass.postal_code}
                  type="text" value={companyState.postal_code} autoComplete="off"
                  onChange={(e) => {
                    updateCompany(e);
                    setChangedDetails({...changedDetails, details: true});
                  }}/>
                <div aria-atomic="true" aria-live="polite">
                  <div className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="companies.obligatory.info"/>
                  </div>
                </div>
              </div>

              <div className={"form-group " + validateCompany('city')}>
                <label htmlFor="city" className="form-label"><FormattedMessage id="companies.city"/></label>
                <input id="city" name="city" className={"form-control " + companyStateClass.city}
                  type="text" value={companyState.city} autoComplete="off"
                  onChange={(e) => {
                    updateCompany(e);
                    setChangedDetails({...changedDetails, details: true});
                  }}/>
                <div aria-atomic="true" aria-live="polite">
                  <div className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="companies.obligatory.info"/>
                  </div>
                </div>
              </div>

              <div className={"form-group " + validateCompany('country')}>
                <label htmlFor="country" className="form-label"><FormattedMessage id="companies.country"/></label>
                <div className="select-group">
                  <select id="country" name="country" className="form-control"
                    value={companyState.country ? companyState.country : ""}
                    onChange={(e) => {
                      updateCompany(e);
                      setChangedDetails({...changedDetails, details: true});
                    }}>
                      {getCountrySelectList()}
                  </select>
                </div>
                <div aria-atomic="true" aria-live="polite">
                  <div className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="companies.obligatory.info"/>
                  </div>
                </div>
              </div>

              <div className={"form-group " + validateCompany('phone')}>
                <label htmlFor="phone" className="form-label"><FormattedMessage id="companies.phone"/></label>
                <input id="phone" name="phone" className={"form-control " + companyStateClass.phone}
                  type="text" value={companyState.phone} autoComplete="off"
                  onChange={(e) => {
                    updateCompany(e);
                    setChangedDetails({...changedDetails, details: true});
                  }}/>
                <div aria-atomic="true" aria-live="polite">
                  <div  className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="companies.obligatory.info"/>
                  </div>
                </div>
              </div>

              <div className={"form-group " + validateCompany('www')}>
                <label htmlFor="www" className="form-label"><FormattedMessage id="companies.www"/></label>
                <input id="www" name="www" className={"form-control " + companyStateClass.www}
                  type="text" value={companyState.www} autoComplete="off"
                  onChange={(e) => {
                    updateCompany(e);
                    setChangedDetails({...changedDetails, details: true});
                  }}/>
                <div aria-atomic="true" aria-live="polite">
                  <div className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="companies.obligatory.info"/>
                  </div>
                </div>
              </div>

              <h2><FormattedMessage id="companies.inquiries"/></h2>

              <div className={"form-group " + validateCompany('ledger_query')}>
                <label htmlFor="ledger_query" className="form-label"><FormattedMessage id="companies.additionalinfo"/></label> 
                <textarea id="ledger_query" name="ledger_query" className={"form-control " + companyBillingStateClass.ledger_query}
                  onChange={(e) => {
                    updateBilling(e);
                    setChangedDetails({...changedDetails, details: true});
                  }}
                  value={companyBillingState.ledger_query ? companyBillingState.ledger_query : ''}>
                </textarea>
                <div aria-atomic="true" aria-live="polite">
                  <div className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="companies.obligatory.info"/>
                  </div>
                </div>
              </div>

            </div>
          </details>

          <details id="invoicing" open={expandInvoicing}>
            <summary onClick={ (e) => { setExpandInvoicing(expandInvoicing => !expandInvoicing); e.preventDefault(); }}
              className={ changedDetails.invoicing ? "changed" : "" }>
                <h2>
                  <FormattedMessage id="companies.invoicing"/>
                </h2>
            </summary>
            <div>

                <h2><FormattedMessage id="companies.billingaddress"/></h2>

                <div className={"form-group " + validateBilling('name')}>
                    <label htmlFor="name" className="form-label"><FormattedMessage id="companies.name"/></label>
                    <input id="name" name="name" className={"form-control " + companyBillingStateClass.name}
                        type="text" value={companyBillingState.name ? companyBillingState.name : ''} autoComplete="off"
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}/>
                    <input id="name2" name="name2" className={"form-control " + companyBillingStateClass.name2}
                        type="text" value={companyBillingState.name2 ? companyBillingState.name2 : ''} autoComplete="off"
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}/>
                    <input id="name3" name="name3" className={"form-control " + companyBillingStateClass.name3}
                        type="text" value={companyBillingState.name3 ? companyBillingState.name3 : ''} autoComplete="off"
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}/>
                    <div aria-atomic="true" aria-live="polite">
                        <div className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="companies.obligatory.info"/>
                        </div>
                    </div>
                </div>

                <div className={"form-group " + validateBilling('address')}>
                    <label htmlFor="address" className="form-label"><FormattedMessage id="companies.address"/></label>
                    <input id="address" name="address" className={"form-control " + companyBillingStateClass.address}
                        type="text" value={companyBillingState.address ? companyBillingState.address : ''} autoComplete="off"
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}/>
                    <div aria-atomic="true" aria-live="polite">
                        <div className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="companies.obligatory.info"/>
                        </div>
                    </div>
                </div>

                <div className={"form-group " + validateBilling('postal_code')}>
                    <label htmlFor="postal_code" className="form-label"><FormattedMessage id="companies.postal_code"/></label>
                    <input id="postal_code" name="postal_code" className={"form-control " + companyBillingStateClass.postal_code}
                        type="text" value={companyBillingState.postal_code ? companyBillingState.postal_code : ''} autoComplete="off"
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}/>
                    <div aria-atomic="true" aria-live="polite">
                        <div className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="companies.obligatory.info"/>
                        </div>
                    </div>
                </div>

                <div className={"form-group " + validateBilling('city')}>
                    <label htmlFor="city" className="form-label"><FormattedMessage id="companies.city"/></label>
                    <input id="city" name="city" className={"form-control " + companyBillingStateClass.city}
                        type="text" value={companyBillingState.city ? companyBillingState.city : ''} autoComplete="off"
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}/>
                    <div aria-atomic="true" aria-live="polite">
                        <div className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="companies.obligatory.info"/>
                        </div>
                    </div>
                </div>

                <div className={"form-group " + validateBilling('country')}>
                    <label htmlFor="country" className="form-label"><FormattedMessage id="companies.country"/></label>
                    <div className="select-group">
                      <select id="country" name="country" className="form-control"
                        value={companyBillingState.country ? companyBillingState.country : ""}
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}>
                          {getCountrySelectList()}
                      </select>
                    </div>
                    <div aria-atomic="true" aria-live="polite">
                        <div className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="companies.obligatory.info"/>
                        </div>
                    </div>
                </div>

                <h2><FormattedMessage id="companies.invoicing"/></h2>

                <fieldset className="form-group radiobutton-group">
                  <legend className="form-label"><FormattedMessage id="companies.billinglanguage"/></legend>
         
                  {/* <div aria-atomic="true" aria-live="polite"><div className="field-error"><i className="fa fa-warning me-2"></i>Pakollinen tieto</div></div> */}
                  <div className="radiogroup form-check">
                    <label className="form-check-label" htmlFor="lang1">
                      <input type="radio" className="form-check-input" id="lang1"
                        name="lang" value="fi"
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}
                        checked={companyBillingState.lang === "fi"}/>
                      <span className="label-text"><FormattedMessage id="language.finnish"/></span>
                    </label>
                  </div>
                  <div className="radiogroup form-check">
                    <label className="form-check-label" htmlFor="lang2">
                      <input type="radio" className="form-check-input" id="lang2"
                        name="lang" value="se"
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}
                        checked={companyBillingState.lang === "se"}/>
                      <span className="label-text"><FormattedMessage id="language.swedish"/></span>
                    </label>
                  </div>
                  <div className="radiogroup form-check">
                    <label className="form-check-label" htmlFor="lang3">
                      <input type="radio" className="form-check-input" id="lang3"
                        name="lang" value="en"
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}
                        checked={companyBillingState.lang === "en"}/>
                      <span className="label-text"><FormattedMessage id="language.english"/></span>
                    </label>
                  </div>

                </fieldset>

                <div className={"form-group " + validateCompany('reporting_interval')}>
                    <label htmlFor="reporting_interval" className="form-label"><FormattedMessage id="companies.reportingperiod"/></label>
                    <div className="select-group">
                        <select id="reporting_interval" name="reporting_interval" className="form-control"
                          value={companyState.reporting_interval ? companyState.reporting_interval : "1"}
                          onChange={(e) => {
                            updateCompany(e);
                            setChangedDetails({...changedDetails, details: true});
                          }}>
                            <option value="1"><FormattedMessage id="companies.reportingperiod.1"/></option>
                            <option value="3"><FormattedMessage id="companies.reportingperiod.3"/></option>
                            <option value="6"><FormattedMessage id="companies.reportingperiod.6"/></option>
                            <option value="12"><FormattedMessage id="companies.reportingperiod.12"/></option>
                        </select>
                    </div>
                    <div aria-atomic="true" aria-live="polite">
                        <div className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="companies.obligatory.info"/>
                        </div>
                    </div>
                </div>

                <div className={"form-group " + validateBilling('vat_rate')}>
                  <label htmlFor="vat_rate" className="form-label"><FormattedMessage id="companies.vatrate"/></label> 
                  <div className="select-group">
                    <select id="vat_rate" name="vat_rate" className="form-control"
                      onChange={(e) => {
                        updateBilling(e);
                        setChangedDetails({...changedDetails, invoicing: true});
                      }}
                      value={companyBillingState.vat_rate ? companyBillingState.vat_rate : ''}>
                      <option value="1"><FormattedMessage id="companies.vatrate.255"/></option>
                      <option value="0"><FormattedMessage id="companies.vatrate.0"/></option>
                    </select>
                  </div>
                  <div aria-atomic="true" aria-live="polite">
                        <div className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="companies.obligatory.info"/>
                        </div>
                    </div>
                </div>

                <div className={"form-group " + validateBilling('payment_term')}>
                  <label htmlFor="payment_term" className="form-label"><FormattedMessage id="companies.paymentterm"/></label> 
                  <div className="select-group">
                    <select id="payment_term" name="payment_term" className="form-control"
                      onChange={(e) => {
                        updateBilling(e);
                        setChangedDetails({...changedDetails, invoicing: true});
                      }}
                      value={companyBillingState.payment_term ? companyBillingState.payment_term : ''}>
                      <option value="15">15 <FormattedMessage id="companies.paymentterm.day"/></option>
                      <option value="30">30 <FormattedMessage id="companies.paymentterm.day"/></option>
                      <option value="45">45 <FormattedMessage id="companies.paymentterm.day"/></option>
                    </select>
                  </div>
                  <div aria-atomic="true" aria-live="polite">
                        <div className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="companies.obligatory.info"/>
                        </div>
                    </div>
                </div>

                <div className={"form-group " + validateBilling('reference')}>
                  <label htmlFor="reference" className="form-label"><FormattedMessage id="companies.reference"/></label> 
                  <input id="reference" name="reference" className={"form-control " + companyBillingStateClass.reference}
                    type="text" value={companyBillingState.reference ? companyBillingState.reference : ''}
                    onChange={(e) => {
                      updateBilling(e);
                      setChangedDetails({...changedDetails, invoicing: true});
                    }}/>
                  <div aria-atomic="true" aria-live="polite">
                        <div className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="companies.obligatory.info"/>
                        </div>
                    </div>
                </div>

                <fieldset className="form-group radiobutton-group">
                  <legend className="form-label"><FormattedMessage id="companies.type"/></legend>
         
                  {/* <div aria-atomic="true" aria-live="polite"><div className="field-error"><i className="fa fa-warning me-2"></i>Pakollinen tieto</div></div> */}
                  <div className="radiogroup form-check">
                    <label className="form-check-label" htmlFor="billingmethod1">
                      <input type="radio" className="form-check-input" id="billingmethod1"
                        name="type" value="1"
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}
                        checked={companyBillingState.type === "1"}/>
                      <span className="label-text"><FormattedMessage id="companies.type.post"/></span>
                    </label>
                  </div>
                  <div className="radiogroup form-check">
                    <label className="form-check-label" htmlFor="billingmethod2">
                      <input type="radio" className="form-check-input" id="billingmethod2"
                        name="type" value="2"
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}
                        checked={companyBillingState.type === "2"}/>
                      <span className="label-text"><FormattedMessage id="companies.type.email"/></span>
                    </label>
                  </div>
                  <div className="radiogroup form-check">
                    <label className="form-check-label" htmlFor="billingmethod3">
                      <input type="radio" className="form-check-input" id="billingmethod3"
                        name="type" value="3"
                        onChange={(e) => {
                          updateBilling(e);
                          setChangedDetails({...changedDetails, invoicing: true});
                        }}
                        checked={companyBillingState.type === "3"}/>
                      <span className="label-text"><FormattedMessage id="companies.type.online"/></span>
                    </label>
                  </div>

                </fieldset>

                <div className="billingmethod_details billingmethod_details2"
                    style={ companyBillingState.type === "2" ? {} : {display: 'none'}}>
                  <div className={"form-group " + validateBilling('email')}>
                    <label htmlFor="email" className="form-label"><FormattedMessage id="companies.billingemail"/></label> 
                    <input id="email" name="email" className={"form-control " + companyBillingStateClass.email} type="text"
                      onChange={(e) => {
                        updateBilling(e);
                        setChangedDetails({...changedDetails, invoicing: true});
                      }}
                      value={companyBillingState.email ? companyBillingState.email : ''}/>
                    <div aria-atomic="true" aria-live="polite">
                        <div className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="companies.obligatory.info"/>
                        </div>
                    </div>
                  </div>
                </div>
                <div className="billingmethod_details billingmethod_details3"
                    style={ companyBillingState.type === "3" ? {} : {display: 'none'}}>
                  <div className={"form-group " + validateBilling('einvoice_id')}>
                    <label htmlFor="einvoice_id" className="form-label"><FormattedMessage id="companies.onlineid"/></label> 
                    <input id="einvoice_id" name="einvoice_id" className={"form-control " + companyBillingStateClass.einvoice_id} type="text"
                      onChange={(e) => {
                        updateBilling(e);
                        setChangedDetails({...changedDetails, invoicing: true});
                      }}
                      value={companyBillingState.einvoice_id ? companyBillingState.einvoice_id : ''}/>
                    <div aria-atomic="true" aria-live="polite">
                        <div className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="companies.obligatory.info"/>
                        </div>
                    </div>
                  </div>

                  <div className={"form-group " + validateBilling('einvoice_operator')}>
                    <label htmlFor="einvoice_operator" className="form-label"><FormattedMessage id="companies.operator"/></label> 
                    <input id="einvoice_operator" name="einvoice_operator" className={"form-control " + companyBillingStateClass.einvoice_operator} type="text"
                      onChange={(e) => {
                        updateBilling(e);
                        setChangedDetails({...changedDetails, invoicing: true});
                      }}
                      value={companyBillingState.einvoice_operator ? companyBillingState.einvoice_operator : ''}/>
                    <div aria-atomic="true" aria-live="polite">
                        <div className="field-error">
                            <i className="fa fa-warning me-2"></i>
                            <FormattedMessage id="companies.obligatory.info"/>
                        </div>
                    </div>
                  </div>
                </div>
            </div>
          </details>

          <details id="membership" open={expandMembership}>
            <summary onClick={ (e) => { setExpandMembership(expandMembership => !expandMembership); e.preventDefault(); }}
              className={ changedDetails.membership ? "changed" : "" }>
                <h2>
                  <FormattedMessage id="companies.membership"/>
                </h2>
            </summary>
            <div>

              <div className={"form-group " + validateCompany('member')}>
                <label htmlFor="member" className="form-label"><FormattedMessage id="companies.member"/></label>
                <input id="member" name="member" className={"form-control " + companyStateClass.member}
                  type="text" value={companyState.member} autoComplete="off"
                  onChange={(e) => {
                    updateCompany(e);
                    setChangedDetails({...changedDetails, membership: true});
                  }}/>
                <div aria-atomic="true" aria-live="polite">
                  <div className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="companies.obligatory.info"/>
                  </div>
                </div>
              </div>
              <div className={"form-group " + validateCompany('certificate')}>
                <label htmlFor="certificate" className="form-label"><FormattedMessage id="companies.certificate"/></label>
                <input id="certificate" name="certificate" className={"form-control " + companyStateClass.certificate}
                  type="text" value={companyState.certificate} autoComplete="off"
                  onChange={(e) => {
                    updateCompany(e);
                    setChangedDetails({...changedDetails, membership: true});
                  }}/>
                <div aria-atomic="true" aria-live="polite">
                  <div className="field-error">
                    <i className="fa fa-warning me-2"></i>
                    <FormattedMessage id="companies.obligatory.info"/>
                  </div>
                </div>
              </div>
              <div className={"form-group " + validateCompany('member_since')}>
                <label className="form-label"><FormattedMessage id="companies.member_since"/></label>
                <div className="highlight_bg">
                <div className="d-flex flex-md-row align-items-md-center flex-column">
                  <DatePicker
                      showPopperArrow={false}
                      selected={companyState.member_since ? new Date( companyState.member_since) : undefined}
                      onChange={(date) => {
                        updateCompany({currentTarget: {name: 'member_since', value: date ? format(date, 'yyyy-MM-dd') : ''}});
                        setChangedDetails({...changedDetails, membership: true});
                      }}
                      customInput={<CalendarInput label="companies.member_since" hideLabel={true} />}
                      name="member_since"
                      id="member_since"
                      dateFormat={'y-MM-dd'}
                      popperModifiers={popperModifiers}
                      locale={lang}
                    />
                </div>
                </div>
              </div>
              <div className={"form-group "}>
                <label htmlFor="reporting_started" className="form-label"><FormattedMessage id="companies.reporting_started"/></label>
                <div className="highlight_bg">
                  {/* <div className={"d-flex flex-md-row align-items-md-center flex-column "}> */}
                  <div className={" "  + validateCompany('reporting_started')}>
                    <DatePicker
                      showPopperArrow={false}
                      selected={companyState.reporting_started ? new Date(companyState.reporting_started) : undefined}
                      onChange={(start) => {
                        updateCompany({currentTarget: {name: 'reporting_started', value: start ? format(start, 'yyyy-MM-dd') : ''}});
                        setChangedDetails({...changedDetails, membership: true}); }}
                      showMonthYearPicker={true}
                      customInput={<CalendarInput label="calendar.select.start"/>}
                      name="startdate"
                      id="startdate"
                      dateFormat='MM/y'
                      popperModifiers={popperModifiers}
                      locale={lang}
                    />
                    </div>
                    <DatePicker
                      showPopperArrow={false}
                      selected={companyState.reporting_ended ? new Date(companyState.reporting_ended) : undefined}
                      onChange={(end) => {
                        updateCompany({currentTarget: {name: 'reporting_ended', value: end ? format(end, 'yyyy-MM-dd') : ''}});
                        setChangedDetails({...changedDetails, membership: true}); }}
                      showMonthYearPicker={true}
                      customInput={<CalendarInput label="calendar.select.end"/>}
                      name="enddate"
                      id="enddate"
                      dateFormat='MM/y'
                      popperModifiers={popperModifiers}
                      locale={lang}
                    />
                  </div>
                {/* </div> */}
              </div>
            </div>
          </details>

          <details id="users" open={expandUsers}>
            <summary onClick={ (e) => { setExpandUsers(expandUsers => !expandUsers); e.preventDefault(); }}
              className={ changedDetails.users ? "changed" : "" }>
                <h2>
                  <FormattedMessage id="companies.addedusers"/>
                </h2>
            </summary>
            <div>
              <div className="toolbar">
                <nav aria-label={ intl.formatMessage({ id: 'section.toolbar' })}>
                  <button className="btn btn-link p-0 btn-add"
                    disabled={companyState?.id ? false : true}
                    onClick={() => setModalActive(true)}>
                      <i className="fa fa fa-plus-circle me-2"></i>
                      <FormattedMessage id="companies.adduser"/>
                  </button>
                </nav>
              </div>
              {!companyState?.id &&
              <p className="info"><i className="fa fa-info-circle me-2"></i>
                <FormattedMessage id="companies.addUser.info"/>
              </p>
              }
              {selectedUsers.length > 0 &&
              <div className="list-group list-group-flush">
                {selectedUsers.map((s:IUserProfile, ind: number) =>
                  getUserItem(s, ind)
                )}
              </div>
              }
            </div>
          </details>

      </>
    );
};